import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell, Building2, ChevronDown, LogOut, Shield,
  Moon, Sun, Search, Command, User as UserIcon, Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore, COLOR_THEMES, type ColorTheme } from '@/store/themeStore';
import { notificationsService, type AssetNotification } from '@/services/notificationsService';
import { organizationsService } from '@/services/organizationsService';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import { useOpenNotification } from '@/hooks/useOpenNotification';
import { CommandPalette } from './CommandPalette';
import { ROUTES } from '@/config/routes';
import AppLauncher from './AppLauncher';
import { getPrimaryRole, ROLE_LABELS } from '@/utils/auth';

interface HeaderProps {
  children?: React.ReactNode;
  /** On mobile, opens the navigation drawer. Absent on desktop. */
  onOpenMobileNav?: () => void;
  /** Sidebar's current width state — owned by MainLayout, not here. */
  collapsed?: boolean;
  /** Absent on mobile: the drawer sidebar always renders expanded, so there's
   *  nothing to toggle. Present means desktop, so the button renders. */
  onToggleCollapsed?: () => void;
}

export function Header({ children, onOpenMobileNav, collapsed, onToggleCollapsed }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const hasRole = useAuthStore((s) => s.hasRole);
  const isPlatformAdmin = hasRole('platform_admin');
  const { theme, setTheme, colorTheme, setColorTheme } = useThemeStore();
  const { open: openNotification } = useOpenNotification();
  
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [notifications, setNotifications] = useState<AssetNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [org, setOrg] = useState<{ name: string; logo: string | null } | null>(null);

  useEffect(() => {
    const orgId = user?.organizationId;
    if (!orgId) {
      setOrg(null);
      return;
    }
    let active = true;
    organizationsService
      .getBranding(orgId)
      .then((data) => {
        if (active) setOrg(data);
      })
      .catch(() => {
        if (active) setOrg(null);
      });
    return () => {
      active = false;
    };
  }, [user?.organizationId]);
  
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const displayName = user?.name ?? user?.email ?? 'User';
  const initials = displayName.slice(0, 2).toUpperCase();
  const primaryRole = getPrimaryRole(user?.roles ?? []);
  const roleLabel = primaryRole ? ROLE_LABELS[primaryRole] : 'User';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifications(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setShowProfile(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setIsNotificationsLoading(true);
    try {
      const response = await notificationsService.list({ page: 1, pageSize: 12 });
      setNotifications(response.notifications);
      setUnreadCount(response.unreadCount ?? 0);
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsNotificationsLoading(false);
    }
  }, [user]);

  // Initial load
  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  // WebSocket integration for real-time notifications
  useNotificationSocket({
    onNotification: (notification) => {
      // Add new notification to the top of the list
      setNotifications((prev) => [notification, ...prev.slice(0, 11)]);
      // Increment unread count if notification is unread
      if (!notification.read) {
        setUnreadCount((prev) => prev + 1);
      }
    },
    onUnreadCount: (count) => {
      // Update unread count from server
      setUnreadCount(count);
    },
    onConnect: () => {
      console.log('[Header] WebSocket connected - real-time notifications enabled');
      // Refresh notifications on reconnect
      void loadNotifications();
    },
    onDisconnect: () => {
      console.log('[Header] WebSocket disconnected - falling back to polling');
    },
  });

  // Global Command Palette Listener (⌘K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await notificationsService.markAllRead();
    } catch {
      void loadNotifications();
    }
  };

  const handleNotificationClick = async (notification: AssetNotification) => {
    // Navigating happens for read and unread alike — the panel is also how you
    // get back to something you already opened. Same target resolution as the
    // Notifications page (see useOpenNotification).
    if (openNotification(notification)?.kind === 'link') {
      setShowNotifications(false);
    }

    if (notification.read) {
      return;
    }

    setNotifications((prev) =>
      prev.map((row) => (row.id === notification.id ? { ...row, read: true } : row)),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await notificationsService.markOneRead(notification.id);
    } catch {
      void loadNotifications();
    }
  };

  const isNirp = location.pathname.startsWith('/nirp');
  const isHome = location.pathname === ROUTES.HOME || location.pathname === '/home';
  const isAnalytics = location.pathname === ROUTES.ANALYTICS;
  const isOrganizations = location.pathname.startsWith(ROUTES.ADMIN_ORGANIZATIONS);
  const isUsers = location.pathname.startsWith(ROUTES.ADMIN_USERS);
  const isTeam = location.pathname.startsWith(ROUTES.TEAM);
  const isDirectory = location.pathname.startsWith(ROUTES.DIRECTORY);
  const isProfile = location.pathname === ROUTES.USER_PROFILE;
  const isMessages = location.pathname === ROUTES.NIRP_MESSAGES;
  const isThreatIntel = location.pathname.startsWith(ROUTES.NIRP_THREAT_FEED) || location.pathname.startsWith(ROUTES.NIRP_ADMIN_THREAT_INTEL);
  const isCms = location.pathname.startsWith('/cms');
  
  let sectionLabel = 'Asset Management';
  // Shown as the page path, but spelled out: /cms/dashboard reads
  // "Case Management System/dashboard". Display only — routes stay /cms/*.
  if (isCms) sectionLabel = location.pathname.substring(1).replace(/^cms(?=\/|$)/, 'Case Management System');
  else if (isHome) sectionLabel = isPlatformAdmin ? 'Admin Home' : 'Home';
  else if (isProfile) sectionLabel = 'User Profile';
  else if (isMessages) sectionLabel = 'Messages';
  else if (isThreatIntel) sectionLabel = 'Threat Intel Feed';
  else if (isNirp) sectionLabel = 'Incident Response';
  else if (isAnalytics) sectionLabel = 'Analytics & Reports';
  else if (isOrganizations) sectionLabel = 'Organizations';
  else if (isUsers) sectionLabel = 'Users';
  else if (isTeam) sectionLabel = 'Team / Users';
  else if (isDirectory) sectionLabel = 'Directory';

  return (
    <>
      <header className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-border bg-background px-4 py-3 md:px-6">
        {/* Left Section: Breadcrumbs / Page Title. min-w-0 lets the title
            truncate instead of pushing the action cluster off-screen on mobile. */}
        <div className="flex min-w-0 flex-1 items-center gap-3 md:gap-4">
          {onOpenMobileNav && (
            <button
              type="button"
              onClick={onOpenMobileNav}
              aria-label="Open navigation"
              className="md:hidden -ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
          {children ? (
            // On mobile the page's title block is constrained to one line so it
            // truncates rather than wrapping into a cramped multi-line column
            // next to the action icons.
            <div className="min-w-0 flex-1 [&_h1]:truncate [&_h2]:truncate [&_p]:truncate [&_p]:hidden sm:[&_p]:block">
              {children}
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {/* The org's own emblem stands in for the generic icon once one
                  has been uploaded; orgs without one keep the icon. The tile
                  goes neutral for a logo — a coloured mark on the primary tint
                  fights with it — and keeps a little inset so the artwork is
                  not flush to the rounded edge. */}
              <div
                className={cn(
                  'h-8 w-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden',
                  org?.logo
                    ? 'bg-background p-1 ring-1 ring-border/60 shadow-sm'
                    : 'bg-primary/10',
                )}
              >
                {org?.logo ? (
                  <img
                    src={org.logo}
                    alt={`${org.name} emblem`}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Command className="h-4 w-4 text-primary" />
                )}
              </div>
              <div className="flex min-w-0 flex-col">
                {/* truncate, not whitespace-nowrap: a long section name must
                    yield space to the action icons on mobile rather than push
                    them off the edge. */}
                <h2 className="truncate text-sm font-semibold text-foreground">
                  {sectionLabel}
                </h2>
                {org?.name && (
                  <p className="hidden sm:block truncate text-xs leading-tight text-muted-foreground">
                    {org.name}
                  </p>
                )}
              </div>
              {/* {onToggleCollapsed && (
                <button
                  type="button"
                  onClick={onToggleCollapsed}
                  title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  className="ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border focus-visible:ring-offset-2"
                >
                  {collapsed ? (
                    <ChevronRight className="h-4 w-4" strokeWidth={2} />
                  ) : (
                    <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                  )}
                </button>
              )} */}
            </div>
          )}
        </div>

        {/* Center Section: Command Bar (Search) */}
        <div className="hidden md:flex flex-2 max-w-md w-full mx-8">
          <div className="relative w-full group cursor-pointer" onClick={() => setShowCommandPalette(true)}>
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
            </div>
            <div className="w-full bg-muted/40 border border-border/60 rounded-xl py-2 pl-10 pr-4 text-sm text-muted-foreground focus-within:ring-2 focus-within:ring-primary/20 focus-within:bg-muted/60 transition-all select-none">
              Search anything...
            </div>
            <div className="absolute inset-y-0 right-3 flex items-center">
              <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-border/60 bg-muted px-1.5 font-mono text-2xs font-medium text-muted-foreground">
                <span className="text-xs">⌘</span>K
              </kbd>
            </div>
          </div>
        </div>

        {/* Right Section: Actions & Profile. shrink-0 so it keeps its size and
            the title truncates instead, rather than the icons being squeezed. */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <div className="h-6 w-px bg-border/40 mx-2 hidden sm:block" />

          {/* Theme Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-9 w-9 rounded-xl transition-all",
                  theme === 'brand' ? "bg-primary/20 text-primary shadow-info-glow" : "hover:bg-muted/80 text-muted-foreground"
                )}
              >
                {theme === 'dark' && <Moon className="h-4 w-4" />}
                {theme === 'light' && <Sun className="h-4 w-4" />}
                {theme === 'brand' && <Shield className="h-4 w-4" />}
                {theme === 'system' && <Command className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 border-border/40">
              <p className="px-2 py-1.5 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Appearance</p>
              <DropdownMenuItem onClick={() => setTheme('light')} className="gap-2 cursor-pointer">
                <Sun className="h-3.5 w-3.5" /> Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')} className="gap-2 cursor-pointer">
                <Moon className="h-3.5 w-3.5" /> Dark (Nexus)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('brand')} className="gap-2 cursor-pointer">
                <Shield className="h-3.5 w-3.5" /> Brand Blue
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('system')} className="gap-2 cursor-pointer">
                <Command className="h-3.5 w-3.5" /> System
              </DropdownMenuItem>

              <div className="my-1 h-px bg-border/40" />

              <p className="px-2 py-1.5 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Accent</p>
              <div className="flex items-center gap-2 px-2 py-2">
                {COLOR_THEMES.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    title={opt.label}
                    aria-label={opt.label}
                    onClick={() => setColorTheme(opt.value as ColorTheme)}
                    className={cn(
                      'h-7 w-7 rounded-full transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                      colorTheme === opt.value && 'ring-2 ring-offset-2 ring-offset-background',
                    )}
                    style={{
                      background: opt.swatch,
                      boxShadow:
                        colorTheme === opt.value
                          ? `0 0 0 2px ${opt.swatch}`
                          : undefined,
                    }}
                  />
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <AppLauncher />

          {/* Notifications */}
          <div ref={notifRef} className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowNotifications(!showNotifications)}
              className="h-9 w-9 rounded-xl hover:bg-muted/80 text-muted-foreground transition-all"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span
                  aria-label={`${unreadCount} unread notifications`}
                  className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-2xs font-semibold leading-none text-primary-foreground ring-2 ring-background"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>

            {showNotifications && (
              <div className="absolute right-0 top-12 w-80 bg-card rounded-xl border border-border shadow-xl z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
                  <p className="text-sm font-semibold text-foreground">Notifications</p>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="text-xs font-medium text-primary hover:underline">Mark all read</button>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {isNotificationsLoading ? (
                    <div className="px-4 py-6 text-xs text-muted-foreground">Loading notifications...</div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-6 text-xs text-muted-foreground">No notifications available.</div>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => void handleNotificationClick(n)}
                        className={cn(
                          'w-full text-left px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 cursor-pointer transition-colors',
                          !n.read && 'bg-primary/5'
                        )}
                      >
                        <div className="flex justify-between items-start">
                          <p className="text-sm font-medium text-foreground">{n.title}</p>
                          {!n.read && <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1" />}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                        <p className="mt-1 text-2xs text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</p>
                      </button>
                    ))
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowNotifications(false);
                    navigate(ROUTES.NIRP_MESSAGES);
                  }}
                  className="w-full px-4 py-2.5 text-center text-xs font-bold text-primary hover:bg-muted/40 border-t border-border/40 transition-colors"
                >
                  See all notifications
                </button>
              </div>
            )}
          </div>

          {/* User Profile */}
          <div ref={profileRef} className="relative ml-1">
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center gap-2 p-1 pl-1.5 rounded-xl hover:bg-muted/80 transition-all border border-transparent hover:border-border/30"
            >
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Avatar className="h-8 w-8 ring-1 ring-border/50">
                    {user?.avatarUrl && (
                      <AvatarImage src={user.avatarUrl} alt={displayName} className="object-cover" />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                </div>
                <div className="text-left hidden lg:block mr-1">
                  <p className="text-sm font-semibold leading-none text-foreground">{displayName}</p>
                  <p className="mt-1 text-2xs font-medium uppercase tracking-wider text-muted-foreground">{roleLabel}</p>
                </div>
              </div>
              <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground/40 transition-transform duration-300", showProfile && "rotate-180")} />
            </button>

            {showProfile && (
              <div className="absolute right-0 top-12 w-64 bg-card rounded-xl border border-border shadow-xl z-50 p-2">
                <div className="px-3 py-4 border-b border-border/40 text-center flex flex-col items-center">
                  <Avatar className="h-16 w-16 mb-3 ring-4 ring-primary/10">
                    {user?.avatarUrl && (
                      <AvatarImage src={user.avatarUrl} alt={displayName} className="object-cover" />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xl">{initials}</AvatarFallback>
                  </Avatar>
                  <p className="text-base font-semibold text-foreground">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                
                <div className="flex flex-col gap-1 p-1 mt-2">
                  <Link to={ROUTES.USER_PROFILE}>
                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors">
                      <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-primary/10">
                        <UserIcon className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">Account</p>
                        <p className="text-sm font-medium text-foreground">View Profile</p>
                      </div>
                    </div>
                  </Link>

                  <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors">
                    {org?.logo ? (
                      <img
                        src={org.logo}
                        alt={`${org.name} logo`}
                        className="h-8 w-8 rounded-lg object-contain bg-background border border-border"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-emerald-500/10">
                        <Building2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">Organization</p>
                      <p className="text-sm font-medium text-foreground truncate max-w-[120px]">{org?.name ?? user?.organizationId ?? '—'}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-border/40 mt-2 p-1">
                  <button
                    onClick={() => { setShowProfile(false); logout(); }}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-destructive hover:bg-destructive/10 transition-all font-medium group"
                  >
                    <LogOut className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                    <span className="text-sm">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <CommandPalette open={showCommandPalette} setOpen={setShowCommandPalette} />
    </>
  );
}


